// @ts-check
/**
 * src/api/sessionToken.js
 *
 * The in-memory session JWT, split out of azureClient.js into its own leaf
 * module. azureClient.js needs the token to attach an Authorization header;
 * errorReporter.js needs it to attribute a background error report to a
 * tenant. Those two previously imported from each other (errorReporter.js
 * called into azureClient.js for the token, azureClient.js called into
 * errorReporter.js to file the report) — a circular import that was believed
 * safe because neither side touched the other's export at module-evaluation
 * time, only inside function bodies invoked later (see GH #2864: a
 * `ReferenceError: Cannot access '<x>' before initialization` in the
 * DocumentEditor chunk traced back to this cycle — Rollup's chunk splitting
 * can still hit a TDZ hazard for a circular pair depending on which chunk
 * evaluates first). Routing both callers through this dependency-free module
 * removes the cycle instead of relying on call-order safety.
 */

import { clearStepUpToken } from './stepUp.js';

/** @type {string | null} */
let _sessionToken = null;

/**
 * The portal JWT, when the surface being rendered is one of the magic-link
 * portals (employee, candidate) rather than the main app.
 *
 * Kept in its own slot, NOT in `_sessionToken`, and deliberately not returned
 * by `getSessionToken()`. `azureClient.js` attaches `getSessionToken()` to
 * every ordinary API call, so folding a portal JWT into that slot would start
 * sending a portal credential to admin endpoints — which every one of them is
 * required to refuse (Azure Function Security Checklist §4) but none should
 * ever be offered. The portals keep calling their own endpoints with their own
 * token exactly as before.
 *
 * The one consumer is `getReportingToken()`: a portal user's browser crash had
 * no credential to attribute itself with, so `errorReporter.send()` bailed and
 * the crash reached nothing at all.
 *
 * @type {string | null}
 */
let _portalToken = null;

/**
 * @param {string | null | undefined} token
 * @returns {void}
 */
export function setSessionToken(token) {
  _sessionToken = token || null;
  // Signing out ends any step-up window with it (§16, ./stepUp.js).
  if (!_sessionToken) clearStepUpToken();
}

/** @returns {string | null} */
export function getSessionToken() {
  return _sessionToken;
}

/** @returns {boolean} */
export function hasSessionToken() {
  return _sessionToken !== null;
}

/**
 * Register the portal JWT for the magic-link surface currently rendering.
 * Called by EmployeeHub / CandidateHub on mount. Pass null on sign-out.
 *
 * @param {string | null | undefined} token
 * @returns {void}
 */
export function setPortalToken(token) {
  _portalToken = token || null;
}

/**
 * The credential a background error report may attribute itself with.
 *
 * The session JWT when there is one; otherwise the portal JWT. Never both, and
 * never anything else — an error report is attributed by a credential the
 * surface ALREADY holds and the server already verifies, so reporting adds no
 * new way to name a tenant. The server reads the tenant from the verified
 * token, never from the report body.
 *
 * @returns {string | null}
 */
export function getReportingToken() {
  return _sessionToken || _portalToken;
}
